import type { CategoryItem, GameTrustScore, TrustScore, TrustScoreCategories, TrustScoreTip } from '@carouselgg/auth/types'
import { isLaggingGameScore } from '@carouselgg/auth/trust-levels'

export type CategoryKey = keyof TrustScoreCategories

/**
 * The parts of every score that come from who you are, not what you play.
 * They read the same in your Carousel score and in each game's.
 */
export const BASELINE_CATEGORIES: readonly CategoryKey[] = ['identity_core', 'maturity', 'social_identity']

/** The parts that only count a game's own history. */
export const GAME_CATEGORIES: readonly CategoryKey[] = ['gaming_identity', 'ranked_match_activity', 'carousel_participation']

/** All six, in the order the breakdown lists them. */
export const CATEGORY_ORDER: readonly CategoryKey[] = [
  'identity_core',
  'ranked_match_activity',
  'gaming_identity',
  'carousel_participation',
  'maturity',
  'social_identity',
]

/**
 * Names and one-line descriptions. Points and maxima are deliberately absent:
 * the weights are admin-editable, so every number on the page comes from the API.
 */
export const CATEGORY_COPY: Record<CategoryKey, { name: string; description: string }> = {
  identity_core: { name: 'Identity', description: 'Email, phone and passkey' },
  ranked_match_activity: { name: 'Ranked play', description: 'Ranked games across all your linked accounts' },
  gaming_identity: { name: 'Gaming accounts', description: 'Game accounts you have linked' },
  carousel_participation: { name: 'Carousel play', description: 'Seasons and matches played on Carousel' },
  maturity: { name: 'Account age', description: 'How long your Carousel and game accounts have existed' },
  social_identity: { name: 'Social', description: 'Discord, Twitch and YouTube' },
}

/** The same three game-only parts, named for one game. */
export function gameCategoryCopy(key: CategoryKey, short: string): { name: string; description: string } {
  switch (key) {
    case 'gaming_identity': return { name: 'Game account', description: `Your linked ${short} accounts` }
    case 'ranked_match_activity': return { name: `Ranked play in ${short}`, description: `Ranked ${short} games on your linked accounts` }
    case 'carousel_participation': return { name: `Carousel play in ${short}`, description: `${short} seasons and matches on Carousel` }
    default: return { name: CATEGORY_COPY[key].name, description: 'Same as your Carousel score' }
  }
}

export function isBaselineCategory(category: string): boolean {
  return (BASELINE_CATEGORIES as readonly string[]).includes(category)
}

/** "Identity · counts for every game" — the line under a tip. */
export function tipCategoryLine(tip: TrustScoreTip): string {
  const copy = CATEGORY_COPY[tip.category as CategoryKey]
  const name = copy?.name ?? ''
  if (isBaselineCategory(tip.category)) return name ? `${name} · counts for every game` : 'Counts for every game'
  return name
}

const SHORT_NAMES: Record<string, string> = {
  'league-of-legends': 'LoL',
  valorant: 'VAL',
  'counter-strike': 'CS2',
  overwatch: 'OW2',
  'rocket-league': 'RL',
  dota2: 'Dota 2',
  pubg: 'PUBG',
}

/** A game's name short enough for a heading: "CS2", "LoL", or its own name if already short. */
export function gameShortName(game: Pick<GameTrustScore, 'game_slug' | 'game_name'>): string {
  const known = SHORT_NAMES[game.game_slug]
  if (known) return known
  if (game.game_name.length <= 10) return game.game_name
  return game.game_name.split(/[\s-]+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join('')
}

/**
 * The single biggest thing still missing from a game's own history, or null
 * when the game-only parts are full.
 */
export function bestNextStep(score: TrustScore): CategoryItem | null {
  let best: CategoryItem | null = null
  for (const key of GAME_CATEGORIES) {
    for (const item of score.categories[key]?.missing_items ?? []) {
      if (!best || item.points > best.points) best = item
    }
  }
  return best
}

/** Whether a game's score trails the Carousel score enough to call out. */
export function isLagging(game: GameTrustScore, overall: TrustScore): boolean {
  return isLaggingGameScore(game.score.score, overall.score, game.score.flags)
}

/** Tips sorted biggest gain first, without changing the caller's array. */
export function biggestFirst(tips: TrustScoreTip[] | null | undefined): TrustScoreTip[] {
  return [...(tips ?? [])].sort((a, b) => b.points - a.points)
}

/**
 * The decay note for a category: the API sends the multiplier it applied, or
 * 0 / nothing when the category counts in full.
 */
export function decayNote(decay: number | undefined): string | null {
  if (!decay || decay <= 0 || decay >= 1) return null
  return `Counts at ×${decay} because of inactivity`
}

/**
 * Button words for a tip. The API's `action` is a code ("register_passkey"),
 * not copy, so it is named here; an unknown code reads as plain words.
 */
const TIP_ACTIONS: Record<string, string> = {
  verify_phone: 'Verify phone',
  verify_email: 'Verify email',
  register_passkey: 'Add passkey',
  link_gaming: 'Link an account',
  play_carousel: 'Find a season',
  link_discord: 'Link Discord',
  link_twitch: 'Link Twitch',
  link_youtube: 'Link YouTube',
}

export function tipActionLabel(action: string): string {
  const known = TIP_ACTIONS[action]
  if (known) return known
  const words = action.replace(/[_-]+/g, ' ').trim()
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Open'
}

/** Only a path on this site: a `redirect` param must not send anyone elsewhere. */
export function safeRedirect(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null
  return value
}
